import React, { useState } from 'react';
import {
  ArrowRight, CheckCircle2, Circle, ExternalLink, FileText, Globe, HelpCircle, Images, LayoutTemplate,
  MessageSquare,
} from 'lucide-react';
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  ConfirmModal, ProgressBar, StatsCard, StatsGrid, StatusBadge, Switch,
} from '../ui';
import { isFilled } from './cmsSiteModel';

// ─── OverviewTab — is the website live, and what is left to set up ───────
//
// The checklist is the answer to "what does my website still show that is
// not mine?". Every item opens the editor that completes it.

export default function OverviewTab({
  settings, settingsBusy, onToggleLive, siteUrl, layoutName, checklist, stats, content, onGo,
}) {
  const [confirmOff, setConfirmOff] = useState(false);
  const done = checklist.filter((i) => i.done).length;
  const galleryCount = (content.gallery?.images || []).filter((img) => isFilled(img?.url)).length;
  const faqCount = [...(content.home?.faqs || []), ...(content.faqs?.items || [])]
    .filter((f) => isFilled(f?.question)).length;

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <AppCard>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4 tw-pt-5">
          <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
            <div className="tw-flex tw-min-w-0 tw-items-start tw-gap-3">
              <span className="tw-inline-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary-soft tw-text-primary" aria-hidden="true">
                <Globe size={20} />
              </span>
              <div className="tw-min-w-0">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                  <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">Your website</h2>
                  <StatusBadge status={settings.cmsEnabled ? 'active' : 'inactive'}>
                    {settings.cmsEnabled ? 'Live' : 'Off'}
                  </StatusBadge>
                </div>
                <a
                  href={siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-mt-0.5 tw-inline-flex tw-items-center tw-gap-1 tw-break-all tw-text-sm tw-text-primary"
                >
                  {siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  <ExternalLink size={14} aria-hidden="true" />
                </a>
              </div>
            </div>
            <Switch
              label="Website is live"
              checked={settings.cmsEnabled}
              disabled={settingsBusy}
              onChange={(next) => (next ? onToggleLive(true) : setConfirmOff(true))}
            />
          </div>
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">
            {settings.cmsEnabled
              ? 'Visitors to your address see your website. Changes you save appear within a minute.'
              : 'Your website is switched off: visitors see an “unavailable” page instead. You can still edit everything here.'}
          </p>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-4 tw-text-sm tw-text-content-muted">
            <LayoutTemplate size={16} aria-hidden="true" />
            Layout: <strong className="tw-text-content">{layoutName}</strong>
            <AppButton size="sm" variant="ghost" onClick={() => onGo({ tab: 'design' })}>
              Change <ArrowRight size={16} />
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Finish setting up</AppCardTitle>
          <AppCardDescription>
            {done === checklist.length
              ? 'Everything on your website is your own. Nice work.'
              : 'Until these are done, your website shows sample content that is not about your institute.'}
          </AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-items-center tw-gap-3">
            <ProgressBar value={checklist.length ? (done / checklist.length) * 100 : 100} className="tw-flex-1" />
            <span className="tw-shrink-0 tw-text-sm tw-tabular-nums tw-text-content-muted">{done} of {checklist.length} done</span>
          </div>
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-p-0">
            {checklist.map((item) => (
              <li
                key={item.id}
                className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-py-3 first:tw-border-t-0 first:tw-pt-0 last:tw-pb-0"
              >
                {item.done
                  ? <CheckCircle2 size={20} className="tw-shrink-0 tw-text-success" aria-hidden="true" />
                  : <Circle size={20} className="tw-shrink-0 tw-text-content-subtle" aria-hidden="true" />}
                <div className="tw-min-w-0 tw-flex-1">
                  <p className={`tw-m-0 tw-text-sm tw-font-medium ${item.done ? 'tw-text-content-muted' : 'tw-text-content'}`}>
                    {item.label}
                    <span className="tw-sr-only">{item.done ? ' — done' : ' — to do'}</span>
                  </p>
                  {!item.done && <p className="tw-m-0 tw-mt-0.5 tw-text-sm tw-text-content-muted">{item.detail}</p>}
                </div>
                <AppButton size="sm" variant={item.done ? 'ghost' : 'outline'} onClick={() => onGo(item.target)}>
                  {item.done ? 'Edit' : 'Do this'} <ArrowRight size={16} />
                </AppButton>
              </li>
            ))}
          </ul>
        </AppCardContent>
      </AppCard>

      <StatsGrid>
        <StatsCard title="Blog posts" value={`${stats.publishedBlogs} published`} icon={FileText} subtitle={`${stats.totalBlogs} in total`} />
        <StatsCard title="Testimonials" value={stats.totalTestimonials} icon={MessageSquare} />
        <StatsCard title="Gallery photos" value={galleryCount} icon={Images} />
        <StatsCard title="FAQs" value={faqCount} icon={HelpCircle} />
      </StatsGrid>

      <ConfirmModal
        show={confirmOff}
        title="Switch your website off?"
        message="Visitors will see an “unavailable” page until you switch it back on. Your content is kept."
        confirmText="Switch off"
        variant="danger"
        onConfirm={() => { setConfirmOff(false); onToggleLive(false); }}
        onHide={() => setConfirmOff(false)}
      />
    </div>
  );
}
